import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserProfileDto, UsersService, UserStatus } from '@buck-e-icp/interfaces';
import { Oauth2SignOutQueryDto } from '@icp/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { IdpService, LoginService } from '../services';
import { AuthActions } from './auth.actions';

@Injectable()
export class AuthEffects {
    private actions$ = inject(Actions);
    private router = inject(Router);
    private idpService = inject(IdpService);
    private usersService = inject(UsersService);
    private loginService = inject(LoginService);

    autoLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.startAutoLogin),
            switchMap(() =>
                this.idpService.getCurrentSession().pipe(
                    switchMap((authSession) => {
                        if (authSession) {
                            const currentProfileId = this.loginService.getUserProfileIdFromStorage();
                            return this.usersService.getCurrentProfiles().pipe(
                                switchMap((profiles: UserProfileDto[]) => {
                                    if (!profiles.length) {
                                        return of(
                                            AuthActions.loginFailed({
                                                error: 'The user has no associated user profile in bucke',
                                            }),
                                        );
                                    }

                                    // A deactivated profile (its tenant was stopped) may never become the active
                                    // profile automatically; only active profiles can be auto-selected.
                                    if (profiles.length === 1 && profiles[0].status === UserStatus.ACTIVE) {
                                        return of(
                                            AuthActions.loginSuccess({
                                                activeProfile: profiles[0],
                                                availableProfiles: profiles,
                                                redirect: false,
                                            }),
                                        );
                                    }

                                    if (currentProfileId) {
                                        const selectedProfile = profiles.find(
                                            (p) => p.id === currentProfileId && p.status === UserStatus.ACTIVE,
                                        );
                                        if (selectedProfile) {
                                            return of(
                                                AuthActions.loginSuccess({
                                                    activeProfile: selectedProfile,
                                                    availableProfiles: profiles,
                                                    redirect: false,
                                                }),
                                            );
                                        }
                                    }

                                    return of(
                                        AuthActions.selectProfile({
                                            profiles,
                                        }),
                                    );
                                }),
                                // for auto login don't show error messages => let them do the login and then give an error message.
                                catchError((error: HttpErrorResponse) =>
                                    of(AuthActions.loginFailed({ error: error.message })),
                                ),
                            );
                        }
                        // will go to /
                        return of(AuthActions.logoutSuccess({ redirect: false }));
                    }),
                ),
            ),
        ),
    );
    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logout),
            switchMap((_) =>
                this.idpService.logout().pipe(
                    map(() => AuthActions.logoutSuccess({ redirect: false })),
                    tap(() => {
                        const url = new URL(`${environment.idpUrl}/idp/oauth2/v1/sign-out`);
                        const params: Oauth2SignOutQueryDto = {
                            client_id: environment.idpClientId!,
                            post_logout_redirect_uri: window.location.origin,
                        };
                        for (const [key, value] of Object.entries(params)) {
                            url.searchParams.append(key, value);
                        }
                        window.location.href = url.toString();
                    }),
                    catchError((error) => of(AuthActions.logoutFailed({ error }))),
                ),
            ),
        ),
    );

    goToStartOnLoginOrLogout = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.logoutSuccess, AuthActions.loginSuccess),
                filter((action) => action.redirect),
                map(() => {
                    return this.router.navigate(['/']);
                }),
            ),
        { dispatch: false },
    );
    removeActiveProfileInStorageOnLogout = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.logoutSuccess),
                map(() => {
                    this.loginService.deleteUserProfileIdFromStorage();
                }),
            ),
        { dispatch: false },
    );
    setActiveProfileOnLogin = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.loginSuccess),
                map((action) => {
                    this.loginService.storeUserProfile(action.activeProfile);
                }),
            ),
        { dispatch: false },
    );

    selectProfile = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.selectProfile),
                map(() => {
                    return this.router.navigate(['/auth/select-profile']);
                }),
            ),
        { dispatch: false },
    );
}
